import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, Loader2, Upload, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    instagram: "",
    preference_contact: "email",
    style: "",
    zone: "",
    taille: "",
    description: "",
    budget_max: "",
    disponibilites: "",
    references_urls: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 5)); // Max 5 images
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.style) {
      toast.error("Veuillez sélectionner un style de tatouage.");
      return;
    }
    if (!formData.preference_contact) {
      toast.error("Veuillez indiquer votre préférence de contact.");
      return;
    }
    
    setLoading(true);

    try {
      // Convert images to base64
      const imagePromises = images.map(img => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({
            name: img.name,
            data: reader.result
          });
          reader.onerror = reject;
          reader.readAsDataURL(img.file);
        });
      });
      
      const imagesBase64 = await Promise.all(imagePromises);
      
      await axios.post(`${API}/project-requests`, {
        nom: formData.nom,
        email: formData.email,
        instagram: formData.instagram,
        preference_contact: formData.preference_contact,
        style: formData.style,
        zone: formData.zone,
        taille: formData.taille,
        description: formData.description,
        budget_max: formData.budget_max,
        disponibilites: formData.disponibilites,
        references_urls: [],
        images: imagesBase64,
      });
      setSuccess(true);
      toast.success("Demande envoyée avec succès !");
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="project-request-success" className="min-h-screen flex items-center justify-center">
        <div className="page-container text-center py-24">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1} />
          <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
            Demande envoyée !
          </h1>
          <p className="text-muted-foreground font-light max-w-md mx-auto mb-8">
            Merci pour votre demande. Je reviendrai vers vous dans les plus brefs délais pour discuter de votre projet.
          </p>
          <Button onClick={() => navigate("/")} variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="project-request-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            Demande de projet
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            Parlons de votre idée
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            Remplissez ce formulaire pour me présenter votre projet. Plus vous serez précis·e, mieux je pourrai vous accompagner.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="pb-16">
        <div className="page-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl">
            <div className="text-center">
              <div className="w-12 h-12 border border-border flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl">1</span>
              </div>
              <h3 className="font-medium mb-2">Demande</h3>
              <p className="text-sm text-muted-foreground">Décrivez votre projet en détail</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 border border-border flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl">2</span>
              </div>
              <h3 className="font-medium mb-2">Échange</h3>
              <p className="text-sm text-muted-foreground">Je vous recontacte pour en discuter</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 border border-border flex items-center justify-center mx-auto mb-4">
                <span className="font-heading text-xl">3</span>
              </div>
              <h3 className="font-medium mb-2">Création</h3>
              <p className="text-sm text-muted-foreground">On planifie ensemble la séance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <form onSubmit={handleSubmit} className="max-w-2xl space-y-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-heading border-b border-border/40 pb-2">
                Vos coordonnées
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <Label htmlFor="nom" className="form-label">Nom / Prénom *</Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-nom"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="instagram" className="form-label">Instagram *</Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  required
                  placeholder="@votre_pseudo"
                  className="form-input"
                  data-testid="input-instagram"
                />
              </div>

              <div className="form-group">
                <Label className="form-label">Préférence de contact *</Label>
                <RadioGroup
                  value={formData.preference_contact}
                  onValueChange={(v) => handleSelectChange("preference_contact", v)}
                  className="flex gap-6 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="contact-email" />
                    <Label htmlFor="contact-email" className="cursor-pointer">Email</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="instagram" id="contact-instagram" />
                    <Label htmlFor="contact-instagram" className="cursor-pointer">Instagram</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-heading border-b border-border/40 pb-2">
                Votre projet
              </h3>

              <div className="form-group">
                <Label className="form-label">Style de tatouage souhaité *</Label>
                <RadioGroup
                  value={formData.style}
                  onValueChange={(v) => handleSelectChange("style", v)}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3"
                >
                  <div className="flex items-start space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="minimaliste" id="style-minimaliste" className="mt-1" />
                    <Label htmlFor="style-minimaliste" className="cursor-pointer flex-1">
                      <span className="font-medium">Minimaliste</span>
                      <span className="text-sm text-muted-foreground block mt-1">
                        Lignes simples, sans ombrages
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="fineline" id="style-fineline" className="mt-1" />
                    <Label htmlFor="style-fineline" className="cursor-pointer flex-1">
                      <span className="font-medium">Fine line</span>
                      <span className="text-sm text-muted-foreground block mt-1">
                        Lignes fines avec légers ombrages
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="micro-realisme" id="style-micro" className="mt-1" />
                    <Label htmlFor="style-micro" className="cursor-pointer flex-1">
                      <span className="font-medium">Micro / semi-réalisme</span>
                      <span className="text-sm text-muted-foreground block mt-1">
                        Détails réalistes, ombrages plus travaillés
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3 p-4 border border-border/40 cursor-pointer hover:border-foreground transition-colors">
                    <RadioGroupItem value="autre" id="style-autre" className="mt-1" />
                    <Label htmlFor="style-autre" className="cursor-pointer flex-1">
                      <span className="font-medium">Autre / Je ne sais pas encore</span>
                      <span className="text-sm text-muted-foreground block mt-1">
                        À préciser dans la description du projet
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-muted-foreground mt-3 italic">
                  Si vous hésitez sur le style, n'hésitez pas à décrire votre projet ou à joindre une référence.
                </p>
              </div>

              <div className="form-group">
                <Label htmlFor="zone" className="form-label">Zone à tatouer *</Label>
                <Select onValueChange={(v) => handleSelectChange("zone", v)} required>
                  <SelectTrigger className="form-input" data-testid="select-zone">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bras">Bras</SelectItem>
                    <SelectItem value="avant-bras">Avant-bras</SelectItem>
                    <SelectItem value="poignet">Poignet</SelectItem>
                    <SelectItem value="main">Main</SelectItem>
                    <SelectItem value="epaule">Épaule</SelectItem>
                    <SelectItem value="dos">Dos</SelectItem>
                    <SelectItem value="cote">Côte</SelectItem>
                    <SelectItem value="jambe">Jambe</SelectItem>
                    <SelectItem value="cheville">Cheville</SelectItem>
                    <SelectItem value="pied">Pied</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="taille" className="form-label">Taille approximative en cm *</Label>
                <Input
                  id="taille"
                  name="taille"
                  value={formData.taille}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 10 cm, 15x20 cm..."
                  className="form-input"
                  data-testid="input-taille"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="description" className="form-label">Description du projet *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  placeholder="Décrivez votre idée, l'ambiance souhaitée, ce qui vous inspire..."
                  className="form-textarea min-h-[150px]"
                  data-testid="input-description"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="budget_max" className="form-label">Budget maximum *</Label>
                <Input
                  id="budget_max"
                  name="budget_max"
                  value={formData.budget_max}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 200€, 300-400€..."
                  className="form-input"
                  data-testid="input-budget"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="disponibilites" className="form-label">
                  Disponibilités de préférence *
                </Label>
                <Textarea
                  id="disponibilites"
                  name="disponibilites"
                  value={formData.disponibilites}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Plutôt en semaine, le matin / Les mercredis et vendredis après-midi / Flexible..."
                  className="form-textarea"
                  data-testid="input-disponibilites"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Indiquez vos préférences de jours et horaires pour faciliter la planification.
                </p>
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <Label className="form-label">Photos d'inspiration</Label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border hover:border-foreground cursor-pointer transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" strokeWidth={1.5} />
                      <p className="text-sm text-muted-foreground">
                        Cliquez pour ajouter des images (max. 5)
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      data-testid="input-images"
                    />
                  </label>
                </div>
                
                {images.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-4">
                    {images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.preview}
                          alt={`Référence ${index + 1}`}
                          className="w-full aspect-square object-cover border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full md:w-auto"
              disabled={loading}
              data-testid="submit-project"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Envoyer ma demande"
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ProjectRequest;
